import { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { loadGoogleMaps, extractAddressParts } from '@/lib/googleMaps';

let tokenCache = null;

export const fetchPlaceDetails = async (suggestion) => {
  const place = suggestion.placePrediction.toPlace();
  await place.fetchFields({ fields: ['location', 'addressComponents', 'displayName', 'formattedAddress'] });
  const parts = extractAddressParts({ address_components: (place.addressComponents || []).map((c) => ({ long_name: c.longText, types: c.types })) });
  tokenCache = null;
  return { ...parts, lat: place.location?.lat(), lng: place.location?.lng(), name: place.displayName, formatted: place.formattedAddress };
};

export const PlaceSearchInput = ({ value, onChange, onSelect, onEnter, placeholder, inputClassName = '', testid = 'place-search-input', icon = true }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const search = (text) => {
    clearTimeout(timer.current);
    if (!text || text.trim().length < 2) { setItems([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const maps = await loadGoogleMaps();
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await maps.importLibrary('places');
        tokenCache = tokenCache || new AutocompleteSessionToken();
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: text, includedRegionCodes: ['in'], sessionToken: tokenCache, language: 'en' });
        setItems(suggestions.filter((s) => s.placePrediction).slice(0, 6));
        setOpen(true);
      } catch { setItems([]); }
    }, 220);
  };

  const pick = async (s) => {
    setBusy(true);
    try {
      const details = await fetchPlaceDetails(s);
      onChange(s.placePrediction.text.text);
      setOpen(false);
      onSelect(details);
    } finally { setBusy(false); }
  };

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-0">
      {icon && <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />}
      <input value={value} onChange={(e) => { onChange(e.target.value); search(e.target.value); }} onFocus={() => items.length && setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); items.length && open ? pick(items[0]) : onEnter?.(); } if (e.key === 'Escape') setOpen(false); }}
        placeholder={placeholder} className={inputClassName} data-testid={testid} autoComplete="off" />
      {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      {open && items.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-1.5 z-[80] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden text-left" data-testid="place-suggestions">
          {items.map((s) => {
            const p = s.placePrediction;
            return (
              <li key={p.placeId}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)} className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 text-left" data-testid="place-suggestion-item">
                  <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                  <span className="min-w-0"><span className="block text-sm font-semibold text-[#0F172A] truncate">{p.mainText?.text || p.text.text}</span>{p.secondaryText?.text && <span className="block text-xs text-slate-500 truncate">{p.secondaryText.text}</span>}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
